import { TraceMetadata, Tracer } from "../tracer";

/**
 * Braintrust tracer adapter
 * 
 * This adapter requires the braintrust package to be installed by the user.
 * It's not included as a direct dependency of the library.
 */
export class BraintrustTracer implements Tracer {
  private readonly options: Record<string, unknown>;
  private readonly braintrustLib: typeof import("braintrust") | null;

  /**
   * Create a new BraintrustTracer
   * 
   * @param options Optional configuration options to pass to Braintrust
   */
  constructor(options: Record<string, unknown> = {}) {
    this.options = options;
    
    // Try to load Braintrust module
    try {
      this.braintrustLib = require("braintrust");
    } catch (error) {
      console.warn("[BraintrustTracer] Could not load braintrust module:", error);
      this.braintrustLib = null;
      return;
    }
    
    // Try to initialize Braintrust if not already done
    try {
      // Only initialize if API key is available
      const apiKey = this.options.apiKey || process.env.BRAINTRUST_API_KEY;
      if (apiKey && this.braintrustLib && typeof this.braintrustLib.initLogger === "function") {
        this.braintrustLib.initLogger({
          apiKey: apiKey as string,
          projectName: options.projectName as string || "default",
          ...this.options
        });
      }
    } catch (error) {
      console.warn("[BraintrustTracer] Failed to initialize logger:", error);
    }
  }

  /**
   * Format tracing options for Braintrust
   * 
   * @param metadata The trace metadata
   * @param runType The type of run (function, tool, llm)
   * @returns Properly formatted options for Braintrust
   */
  private formatOptions(metadata: TraceMetadata, runType: string = "function"): Record<string, any> {
    // Remove name from base options since it's used separately
    const { name, input, apiKey, ...otherMetadata } = metadata;
    
    // Base options
    const options = {
      type: runType,
      name: name,
      ...otherMetadata,
    };

    return options;
  }

  /**
   * Create a traceable function using Braintrust
   * 
   * @param fn The function to trace
   * @param metadata Metadata about the trace
   * @returns The traced function
   */
  traceable<T extends (...args: any[]) => any>(
    fn: T,
    metadata: TraceMetadata
  ): T {
    if (!this.braintrustLib) {
      return fn;
    }
    
    try {
      // Check if we have the appropriate wrapper functions
      if (this.braintrustLib && typeof this.braintrustLib.wrapTraced === "function") {
        // Get the run type, defaulting to "function"
        const runType = metadata.run_type || "function";
        
        // Format options for Braintrust
        const options = this.formatOptions(metadata, runType);
        
        // For tools, we need to make sure we handle input/output specifically
        if (runType === "tool") {
          return (async (...args: any[]) => {
            try {

              const result = await fn(...args);
              const currentSpan = this.braintrustLib!.currentSpan;
              // Create a wrapped function that properly logs to Braintrust
              const tracedFn = this.braintrustLib!.wrapTraced(
                // We need to name the function for Braintrust to properly name the span
                Object.defineProperty(
                  async function namedToolFunction(_args: any) {
                    currentSpan().log({
                      metadata: options
                    });
                    return result;
                  },
                  'name',
                  { ...args }
                ),
                options
              );
              const tracedParams = {
                input: metadata.input || args[0] || {},
                output: result,
                metadata: {
                  tool_name: metadata.name,
                  ...metadata
                }
              }

              return tracedFn(tracedParams.input);
            } catch (error) {
              console.error(`[BraintrustTracer] Error in tool '${metadata.name}':`, error);
              throw error;
            }
          }) as unknown as T;
        }
        
        // For LLM calls, handle input/output differently
        if (runType === "llm") {
          // Use the provided aggregator if available
          if (metadata.aggregator) {
            // Wrap the function using wrapTraced
            const currentSpan = this.braintrustLib!.currentSpan;
            const wrappedFn = this.braintrustLib.wrapTraced(
              // We need to name the function for Braintrust to properly name the span
              Object.defineProperty(
                function wrappedLLMFunction(...args: any[]) {
                  currentSpan().log({
                    metadata: options
                  });
                  return fn(...args);
                },
                'name',
                { value: metadata.name || 'anonymous_llm' }
              ),
              options
            );
            
            return wrappedFn as T;
          }
        }
        
        // Default case for other function types
        const wrappedFn = this.braintrustLib.wrapTraced(
          // We need to name the function for Braintrust to properly name the span
          Object.defineProperty(
            function wrappedFunction(...args: any[]) {
              return fn(...args);
            },
            'name',
            { value: metadata.name || 'anonymous_function' }
          ),
          options
        );
        
        return wrappedFn as T;
      }
      
      // Fallback to traced if wrapTraced is not available
      if (this.braintrustLib && typeof this.braintrustLib.traced === "function") {

        // Based on the Braintrust docs, traced should be used like this:
        return (async (...args: any[]) => {
          return await this.braintrustLib!.traced(() => {
            return (async () => {
              try {
                // Call the original function
                const result = await fn(...args);

                return result;
              } catch (error) {
                console.error(`[BraintrustTracer] Error in trace for '${metadata.name}':`, error);
                throw error;
              }
            })();
          });
        }) as unknown as T;
      }
      
      // Fallback to the original function if no tracing method is available
      console.warn(`[BraintrustTracer] No compatible tracing method found in braintrust module`);
      return fn;
    } catch (error) {
      console.warn(`[BraintrustTracer] Error setting up tracing:`, error);
      return fn;
    }
  }
}

/**
 * Helper function to register the Braintrust tracer with the tracing registry
 * 
 * @param options Optional configuration options to pass to Braintrust
 * @returns true if successfully registered, false otherwise
 */
export function registerBraintrustTracer(options: Record<string, unknown> = {}): boolean {
  try {
    // First, ensure braintrust is installed
    require.resolve("braintrust");
    
    // Add API key from environment if not specified in options
    const apiKey = options.apiKey || process.env.BRAINTRUST_API_KEY;
    if (!apiKey) {
      console.warn("[BraintrustTracer] API key not found. Set BRAINTRUST_API_KEY environment variable or pass apiKey in options");
    }
    
    // Import the registry dynamically to avoid circular dependencies
    const { TracerRegistry } = require("../registry");
    const registry = TracerRegistry.getInstance();
    
    // Register the Braintrust tracer with API key included
    registry.register("braintrust", new BraintrustTracer({
      apiKey: apiKey,
      ...options
    }));
    
    return true;
  } catch (error) {
    console.warn("[BraintrustTracer] Failed to register tracer. Make sure braintrust is installed:", error);
    return false;
  }
} 